export interface Starter {
  label: string
  message: string
  /**
   * Optional interaction kind. When omitted, the starter renders as a plain
   * button that submits `message` on click.
   *
   * - "search": renders as an inline input. The user's typed value is
   *   substituted into `message` via the `{query}` placeholder before submit.
   */
  kind?: "search"
}

const escapeRegex = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

/** Comma-separated `applianceType` values from the host (e.g. "refrigerator, freezer"). */
const splitApplianceTypes = (applianceType: string | undefined): string[] =>
  applianceType
    ? applianceType
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : []

/**
 * Natural phrasing for starters when multiple appliance types apply
 * (host still passes a single comma-separated string).
 */
const appliancePhraseForStarters = (
  applianceType: string | undefined,
): string => {
  const parts = splitApplianceTypes(applianceType)
  if (parts.length === 0) return "appliance"
  if (parts.length === 1) return parts[0]!
  if (parts.length === 2) return `${parts[0]} or ${parts[1]}`
  return `${parts.slice(0, -1).join(", ")}, or ${parts[parts.length - 1]}`
}

const getStarterApplianceOptions = (
  applianceType: string | undefined,
): string[] => {
  const parts = splitApplianceTypes(applianceType)
  if (parts.length === 0) return ["appliance"]
  if (parts.length === 1) return parts
  return parts
}

const normalizeSymptom = (
  symptom: string | undefined,
  applianceType: string | undefined,
): string | undefined => {
  if (!symptom) return symptom

  const trimmed = symptom.trim()
  const types = splitApplianceTypes(applianceType)
  if (!trimmed || types.length === 0) return trimmed

  // Strip any listed appliance prefix (e.g. multi-type PDP) to avoid duplication.
  let working = trimmed
  for (const appliance of types) {
    const withoutAppliancePrefix = working.replace(
      new RegExp(
        `^(?:(?:my|the|a|an)\\s+)?${escapeRegex(appliance)}\\s+(?:is\\s+)?`,
        "i",
      ),
      "",
    )
    const next = withoutAppliancePrefix.trim()
    if (next) working = next
  }

  return working.trim() || trimmed
}

const DEFAULT_STARTERS: Starter[] = [
  {
    label: "My washing machine won't drain",
    message: "My washing machine won't drain",
  },
  {
    label: "Refrigerator not cooling properly",
    message: "My refrigerator is not cooling properly",
  },
  {
    label: "Oven not heating up",
    message: "My oven is not heating up",
  },
  {
    kind: "search",
    label: "Search for a part",
    message: "Help me find this part: {query}",
  },
]

/**
 * Returns conversation starters appropriate for the current page context.
 *
 * Priority:
 * 1. Explicit `context.starters` override - used as-is.
 * 2. Auto-generated starters based on `pageType` + metadata.
 * 3. Generic defaults when no context is available.
 */
export const getContextualStarters = (
  context: Ri2PageContext | null | undefined,
): Starter[] => {
  if (!context) return DEFAULT_STARTERS

  // 1. Explicit override
  if (context.starters) {
    return context.starters
  }

  const {
    pageType,
    modelNumber,
    brand,
    applianceType,
    partName,
    partNumber,
    symptom,
  } = context
  const appliance = appliancePhraseForStarters(applianceType)
  const applianceOptions = getStarterApplianceOptions(applianceType)
  const normalizedSymptom = normalizeSymptom(symptom, applianceType)
  const brandStr = brand ? `${brand} ` : ""
  const modelTag = modelNumber ? ` (${modelNumber})` : ""

  // 2. Model / appliance page
  if (pageType === "model") {
    if (normalizedSymptom) {
      return [
        {
          label: `Why is my ${appliance} ${normalizedSymptom}?`,
          message: `Why is my ${brandStr}${appliance}${modelTag} ${normalizedSymptom}?`,
        },
        {
          label: `How do I fix a ${appliance} that is ${normalizedSymptom}?`,
          message: `How do I fix a ${brandStr}${appliance}${modelTag} that is ${normalizedSymptom}?`,
        },
        {
          label: `What parts could cause ${normalizedSymptom}?`,
          message: `What parts could cause a ${brandStr}${appliance}${modelTag} to be ${normalizedSymptom}?`,
        },
      ]
    }
    return [
      {
        label: `My ${brandStr}${appliance} won't start`,
        message: `My ${brandStr}${appliance}${modelTag} won't start`,
      },
      {
        label: "Help me find the right part",
        message: `I need help finding the right part for my ${brandStr}${appliance}${modelTag}`,
      },
    ]
  }

  // 3. Part page
  if (pageType === "part" && partName) {
    const partTag = partNumber ? ` (${partNumber})` : ""
    if (normalizedSymptom) {
      if (applianceOptions.length > 1) {
        return [
          {
            label: "How do I install this part?",
            message: `How do I install the ${partName}${partTag}?`,
          },
          {
            label: "Is this the right part for my appliance?",
            message: `Is the ${partName}${partTag} the right part for my appliance?`,
          },
          ...applianceOptions.slice(0, 2).map((option) => ({
            label: `Could the ${partName} cause ${normalizedSymptom}?`,
            message: `Could the ${partName}${partTag} be causing my ${option} to be ${normalizedSymptom}?`,
          })),
        ]
      }

      return [
        {
          label: `Could the ${partName} cause ${normalizedSymptom}?`,
          message: `Could the ${partName}${partTag} be causing my ${appliance} to be ${normalizedSymptom}?`,
        },
        {
          label: "Is this the right part for me?",
          message: `Is the ${partName}${partTag} the right part for my ${appliance}?`,
        },
        {
          label: "How do I install this part?",
          message: `How do I install the ${partName}${partTag}?`,
        },
      ]
    }
    if (applianceOptions.length > 1) {
      return [
        {
          label: "How do I install this part?",
          message: `How do I install the ${partName}${partTag}?`,
        },
        {
          label: "Is this the right part for my appliance?",
          message: `Is the ${partName}${partTag} the right part for my appliance?`,
        },
        ...applianceOptions.slice(0, 2).map((option) => ({
          label: `My ${option} has an issue`,
          message: `My ${option} has an issue - could the ${partName} be the problem?`,
        })),
      ]
    }

    return [
      {
        label: "Is this the right part for me?",
        message: `Is the ${partName}${partTag} the right part for my ${appliance}?`,
      },
      {
        label: "How do I install this part?",
        message: `How do I install the ${partName}${partTag}?`,
      },
      {
        label: `My ${appliance} has an issue`,
        message: `My ${appliance} has an issue - could the ${partName} be the problem?`,
      },
    ]
  }

  // 4. Symptom page (standalone - no model or part)
  if (pageType === "symptom" && normalizedSymptom) {
    return [
      {
        label: `Why is my ${appliance} ${normalizedSymptom}?`,
        message: `Why is my ${appliance} ${normalizedSymptom}?`,
      },
      {
        label: `How do I fix a ${appliance} that is ${normalizedSymptom}?`,
        message: `How do I fix a ${appliance} that is ${normalizedSymptom}?`,
      },
      {
        label: `What parts could cause ${normalizedSymptom}?`,
        message: `What parts could cause a ${appliance} to be ${normalizedSymptom}?`,
      },
    ]
  }

  // 5. Fallback
  return DEFAULT_STARTERS
}
